import * as React from 'react'
import { DateTime } from 'luxon'
import { CalendarIcon } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

interface DatePickerProps {
  date?: Date
  onDateChange?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  dateFormat?: string
}

export function DatePicker({
  date,
  onDateChange,
  placeholder = 'Pick a date',
  disabled = false,
  className,
  dateFormat = 'PPP',
}: DatePickerProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={'outline'}
          className={cn(
            'w-full justify-start text-left font-normal',
            !date && 'text-muted-foreground',
            className
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? (dateFormat === 'PPP' ? DateTime.fromJSDate(date).toLocaleString(DateTime.DATE_FULL) : DateTime.fromJSDate(date).toFormat(dateFormat)) : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar mode="single" selected={date} onSelect={onDateChange} defaultMonth={date} />
      </PopoverContent>
    </Popover>
  )
}

interface TimePickerProps {
  time?: Date
  onTimeChange?: (time: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  minuteStep?: number
}

export function TimePicker({
  time,
  onTimeChange,
  placeholder = 'Pick a time',
  disabled = false,
  className,
  minuteStep = 15,
}: TimePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false)

  const generateTimeOptions = () => {
    const times: { value: string; label: string }[] = []
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += minuteStep) {
        const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
        const date = new Date()
        date.setHours(hour, minute, 0, 0)
        times.push({
          value: timeStr,
          label: DateTime.fromJSDate(date).toFormat('h:mm a'),
        })
      }
    }
    return times
  }

  const timeOptions = generateTimeOptions()
  const selectedTimeStr = time ? DateTime.fromJSDate(time).toFormat('HH:mm') : ''

  const handleTimeSelect = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':').map(Number)
    const newTime = new Date()
    newTime.setHours(hours, minutes, 0, 0)
    onTimeChange?.(newTime)
    setIsOpen(false)
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={'outline'}
          className={cn(
            'w-full justify-start text-left font-normal',
            !time && 'text-muted-foreground',
            className
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {time ? DateTime.fromJSDate(time).toFormat('h:mm a') : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <div className="max-h-60 overflow-y-auto">
          {timeOptions.map((option) => (
            <button
              key={option.value}
              className={cn(
                'w-full px-3 py-2 text-left hover:bg-accent hover:text-accent-foreground',
                selectedTimeStr === option.value &&
                  'bg-accent text-accent-foreground'
              )}
              onClick={() => handleTimeSelect(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

interface DateTimePickerProps {
  date?: Date
  onDateChange?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  showTime?: boolean
  minuteStep?: number
}

export function DateTimePicker({
  date,
  onDateChange,
  placeholder = 'Pick a date and time',
  disabled = false,
  className,
  showTime = true,
  minuteStep = 15,
}: DateTimePickerProps) {
  const handleDateChange = (newDate: Date | undefined) => {
    if (!newDate) {
      onDateChange?.(undefined)
      return
    }

    if (date) {
      // Preserve the time when changing date
      newDate.setHours(date.getHours(), date.getMinutes(), date.getSeconds())
    }
    onDateChange?.(newDate)
  }

  const handleTimeChange = (newTime: Date | undefined) => {
    if (!newTime || !date) return

    const updatedDate = new Date(date)
    updatedDate.setHours(newTime.getHours(), newTime.getMinutes(), 0, 0)
    onDateChange?.(updatedDate)
  }

  if (!showTime) {
    return (
      <DatePicker
        date={date}
        onDateChange={onDateChange}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
      />
    )
  }

  return (
    <div className={cn('flex gap-2', className)}>
      <DatePicker
        date={date}
        onDateChange={handleDateChange}
        placeholder="Pick a date"
        disabled={disabled}
        className="flex-1"
      />
      <TimePicker
        time={date}
        onTimeChange={handleTimeChange}
        placeholder="Pick a time"
        disabled={disabled}
        className="flex-1"
        minuteStep={minuteStep}
      />
    </div>
  )
}
